import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext.js';

const SUBSTACK_URL = 'https://theardenresearch.substack.com';

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const parseRssFeed = (xmlText) => {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  const items = Array.from(doc.querySelectorAll('item'));

  return items.map((item) => {
    const get = (tag) => item.querySelector(tag)?.textContent?.trim() || '';

    const enclosure = item.querySelector('enclosure');
    const mediaThumbnail = item.querySelector('thumbnail');
    const thumbnail =
      enclosure?.getAttribute('url') ||
      mediaThumbnail?.getAttribute('url') ||
      null;

    const rawDescription = get('description');
    const excerpt = stripHtml(rawDescription).slice(0, 180).trimEnd();

    return {
      title: get('title'),
      link: get('link'),
      excerpt: excerpt ? excerpt + '…' : '',
      date: get('pubDate'),
      author: get('creator') || 'The Arden Research',
      thumbnail,
    };
  });
};

const BlogCard = ({ post, isLight }) => (
  <a
    href={post.link}
    target="_blank"
    rel="noopener noreferrer"
    className={`group glass-panel rounded-xl overflow-hidden flex flex-col transition-all hover:scale-[1.02] ${
      isLight ? 'hover:shadow-lg hover:shadow-primary/5' : 'hover:border-accent/20'
    }`}
  >
    {post.thumbnail && (
      <div className="w-full h-44 overflow-hidden">
        <img
          src={post.thumbnail}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
    )}
    <div className="flex flex-col flex-1 p-6 sm:p-7 gap-3">
      <h3 className={`text-base sm:text-lg font-light leading-snug ${
        isLight ? 'text-gray-900' : 'text-white'
      }`}>
        {post.title}
      </h3>
      {post.excerpt && (
        <p className={`text-sm leading-relaxed flex-1 ${
          isLight ? 'text-gray-500' : 'text-white/50'
        }`}>
          {post.excerpt}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        <span className={`text-[10px] font-mono uppercase tracking-widest ${
          isLight ? 'text-gray-400' : 'text-white/30'
        }`}>
          {post.date ? formatDate(post.date) : ''}
        </span>
        <span className={`material-symbols-outlined text-sm transition-transform group-hover:translate-x-1 ${
          isLight ? 'text-primary' : 'text-accent'
        }`}>
          arrow_forward
        </span>
      </div>
    </div>
  </a>
);

const SkeletonCard = ({ isLight }) => (
  <div className="glass-panel rounded-xl overflow-hidden">
    <div className={`w-full h-44 animate-pulse ${isLight ? 'bg-gray-200' : 'bg-white/5'}`} />
    <div className="p-6 flex flex-col gap-3">
      <div className={`h-5 rounded animate-pulse w-4/5 ${isLight ? 'bg-gray-200' : 'bg-white/5'}`} />
      <div className={`h-4 rounded animate-pulse w-full ${isLight ? 'bg-gray-100' : 'bg-white/5'}`} />
      <div className={`h-4 rounded animate-pulse w-2/3 ${isLight ? 'bg-gray-100' : 'bg-white/5'}`} />
    </div>
  </div>
);

const BlogPage = () => {
  const { isLight } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const FEED = 'https://theardenresearch.substack.com/feed';

    const tryFetch = (url) =>
      fetch(url).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      });

    // Production: /api/blog/feed/ is same-origin (no CORS needed).
    // Dev fallback: CORS proxies when Django isn't running locally.
    tryFetch('/api/blog/feed/')
      .catch(() => tryFetch(`https://corsproxy.io/?${encodeURIComponent(FEED)}`))
      .catch(() => tryFetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(FEED)}`))
      .then((xml) => {
        const parsed = parseRssFeed(xml);
        setPosts(parsed);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const accentColor = isLight ? 'text-primary' : 'text-accent';
  const accentBg = isLight
    ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary hover:text-white'
    : 'bg-accent/10 border-accent/30 text-accent hover:bg-accent hover:text-black';

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 md:px-12">

        {/* Back Navigation */}
        <Link
          to="/"
          className={`inline-flex items-center gap-2 text-xs sm:text-sm font-mono uppercase tracking-widest transition-colors mb-8 sm:mb-12 ${
            isLight ? 'text-gray-400 hover:text-primary' : 'text-white/50 hover:text-accent'
          }`}
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Back to Home
        </Link>

        {/* Page Header */}
        <div className="mb-10 sm:mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className={`w-1.5 h-1.5 rounded-full ${
                isLight ? 'bg-primary shadow-[0_0_8px_#5e19e6]' : 'bg-accent shadow-[0_0_8px_#00f0ff]'
              }`} />
              <span className={`text-[9px] sm:text-[10px] font-mono uppercase tracking-widest ${accentColor}`}>
                Quant Research
              </span>
            </div>

            <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] mb-4 sm:mb-6 ${
              isLight ? 'text-gray-900' : 'text-white'
            }`}>
              Research <br />
              <span className={`font-bold text-transparent bg-clip-text bg-gradient-to-r ${
                isLight ? 'from-gray-900 via-gray-500 to-gray-300' : 'from-white via-white/70 to-white/20'
              }`}>
                Blog.
              </span>
            </h1>

            <p className={`text-base sm:text-lg font-light leading-relaxed max-w-[55ch] ${
              isLight ? 'text-gray-500' : 'text-white/60'
            }`}>
              Quantitative research, market analysis, and strategy from The Arden Research team.
            </p>
          </div>

          <a
            href={SUBSTACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`group flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 border rounded-lg text-xs font-mono uppercase tracking-widest transition-all ${accentBg}`}
          >
            <span className="material-symbols-outlined text-sm">rss_feed</span>
            Subscribe on Substack
            <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-0.5">
              open_in_new
            </span>
          </a>
        </div>

        {/* Content */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} isLight={isLight} />)}
          </div>
        )}

        {error && (
          <div className="glass-panel rounded-xl p-8 sm:p-12 text-center">
            <span className={`material-symbols-outlined text-4xl mb-4 block ${accentColor}`}>
              wifi_off
            </span>
            <p className={`text-sm font-mono mb-6 ${isLight ? 'text-gray-500' : 'text-white/50'}`}>
              Could not load posts. Read them directly on Substack.
            </p>
            <a
              href={SUBSTACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 px-5 py-3 border rounded-lg text-xs font-mono uppercase tracking-widest transition-all ${accentBg}`}
            >
              Open Substack
              <span className="material-symbols-outlined text-sm">open_in_new</span>
            </a>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="glass-panel rounded-xl p-8 sm:p-12 text-center">
            <p className={`text-sm font-mono ${isLight ? 'text-gray-500' : 'text-white/50'}`}>
              No posts yet — check back soon.
            </p>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <BlogCard key={post.link} post={post} isLight={isLight} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default BlogPage;
